import { motion } from "framer-motion";
import CloseIcon from "assets/icons/close.svg";
import GiftCardBig from "assets/giftcardBig.jpg";
import { Button } from "components/button";
import { useRetrieveGiftcard } from "api/use-giftcard";
import { useMemo } from "react";

// Loading skeleton with gradient animation
const LoadingSkeleton = ({ width = "w-48", height = "h-6" }) => (
  <div
    className={`${width} ${height} bg-gray-300/25 rounded-md overflow-hidden relative`}
  >
    <div className="absolute inset-0 bg-gradient-to-r from-gray-300/25 via-gray-400/25 to-gray-300/25 animate-pulse"></div>
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer"></div>
  </div>
);

export default function GiftcardModal({
  cardNumber = "",
  cardPin = "",
  title,
  takebackId,
  setModalOpen = () => {},
}) {
  // Use the new API hook to fetch giftcard data
  const {
    data: giftcardData,
    isLoading,
    error,
  } = useRetrieveGiftcard(
    { takebackId },
    {
      enabled: !!takebackId,
      refetchOnWindowFocus: false,
    }
  );

  // Extract giftcard details from the API response
  const giftcardDetails = useMemo(() => {
    if (!giftcardData?.data?.[0]?.payments_metadatas?.items) {
      return { code: cardNumber, pin: cardPin };
    }

    const metadata = giftcardData.data[0].payments_metadatas.items;
    const metadataMap = metadata.reduce((acc, item) => {
      acc[item.key] = item.value;
      return acc;
    }, {});

    return {
      code: metadataMap.gift_card_number,
      pin: metadataMap.cardPin || cardPin,
      amount: metadataMap.gift_card_amount,
      currency: metadataMap.gift_card_currency,
      expiry: metadataMap.gift_card_expiry,
      status: metadataMap.status,
    };
  }, [giftcardData, cardNumber, cardPin]);

  const isLoadingData = isLoading && !!takebackId;

  return (
    <div className="fixed w-screen h-screen top-0 left-0 z-50 flex items-center justify-center">
      <motion.div
        className="bg-[#000000b6] w-full h-full fixed"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={() => setModalOpen(false)}
      ></motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
        className="bg-white scrollbar-hide rounded-xl w-[min(500px,90vw)] mx-auto overflow-scroll p-6 relative flex flex-col items-center"
      >
        <div
          className="absolute top-6 right-6 cursor-pointer z-50"
          onClick={() => setModalOpen(false)}
        >
          <img src={CloseIcon} alt="" />
        </div>
        <div className="relative flex flex-col items-center justify-center w-full overflow-hidden">
          <img src={GiftCardBig} alt="Gift Card" className="mt-8 w-[60%]" />
          <h2 className="text-5xl font-bold mt-8">{title}</h2>

          <div className="w-full space-y-4 md:py-12 md:px-8 flex flex-col items-center justify-center">
            <div className="flex gap-4 items-start">
              <div className="text-gray-600 text-sm font-medium">CODE:</div>
              <div className="flex-1">
                {isLoadingData ? (
                  <LoadingSkeleton width="w-full" height="h-16" />
                ) : (
                  <div className="font-mono text-sm break-all overflow-wrap-anywhere leading-relaxed">
                    {giftcardDetails.code}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <div className="text-gray-600 text-sm font-medium">PIN:</div>
              <div className="flex-1">
                {isLoadingData ? (
                  <LoadingSkeleton width="w-16" height="h-6" />
                ) : (
                  <div className="font-mono text-sm">
                    {giftcardDetails.pin
                      ? giftcardDetails.pin
                      : "(PIN NOT SET IN TEST ENV)"}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {error && (
          <div className="mt-2 text-red-600 text-sm">
            Unable to load giftcard details. Please try again later.
          </div>
        )}

        <Button className="mt-4 w-full" onClick={() => setModalOpen(false)}>
          Done
        </Button>
      </motion.div>
    </div>
  );
}
